import { Select } from "antd";
import type { ColumnType } from "antd/es/table";
import type { SortOrder } from "antd/es/table/interface";
import { EmployeeStatus } from "../../utils/types";
import { isBooleanDropdown } from "../../utils/constants";

// Filter on the employee's "Active" flag from User management, shared by
// every list that centralizes requests/data about people (User management
// itself excluded). Defaults to active employees only.
export type EmployeeStatusFilterValue = EmployeeStatus | "ALL";

export const DEFAULT_EMPLOYEE_STATUS_FILTER: EmployeeStatusFilterValue =
  "ACTIVE";

// Yes/No labels come from the same list as the "Active" field in User
// management (isBooleanDropdown), so the wording stays 1:1.
const activeLabel = (status: EmployeeStatus) =>
  isBooleanDropdown.find((item) => item.value === (status === "ACTIVE"))
    ?.text ?? "-";

export const EMPLOYEE_STATUS_OPTIONS: { label: string; value: EmployeeStatusFilterValue }[] = [
  { label: activeLabel("ACTIVE"), value: "ACTIVE" },
  { label: activeLabel("INACTIVE"), value: "INACTIVE" },
  { label: "All", value: "ALL" },
];

export const matchesEmployeeStatus = (
  status: EmployeeStatus | undefined,
  filter: EmployeeStatusFilterValue,
) => filter === "ALL" || status === filter;

// Value for export endpoints: undefined means "all employees".
export const employeeStatusParam = (filter: EmployeeStatusFilterValue) =>
  filter === "ALL" ? undefined : filter;

type Props = {
  value: EmployeeStatusFilterValue;
  onChange: (value: EmployeeStatusFilterValue) => void;
  className?: string;
};

export const EmployeeStatusFilter = ({ value, onChange, className }: Props) => (
  <div className={`flex items-center gap-2 ${className ?? ""}`}>
    <span className="whitespace-nowrap">Active</span>
    <Select
      value={value}
      onChange={onChange}
      options={EMPLOYEE_STATUS_OPTIONS}
      style={{ minWidth: 110 }}
    />
  </div>
);

// "Active" column, same Yes/No wording as User management.
export const activeColumn = <
  T extends { employeeStatus?: EmployeeStatus },
>(): ColumnType<T> => ({
  title: "Active",
  dataIndex: "employeeStatus",
  key: "employeeStatus",
  width: 90,
  sorter: (a: T, b: T) =>
    (a.employeeStatus || "").localeCompare(b.employeeStatus || ""),
  sortDirections: ["ascend", "descend"] as SortOrder[],
  render: (value?: EmployeeStatus) =>
    value ? activeLabel(value) : "-",
});
